import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Star, Plus, Coins } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

const FanTiersTab = () => {
  const { user } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: 'Fan Tier', price_tokens_monthly: 200, tokens_included: 0,
    free_content_threshold: 0, benefits: '',
  });

  const load = async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API}/api/subscriptions/tiers/${user.id}`, { withCredentials: true });
      setTiers(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, [user?.id]);

  const create = async () => {
    if (!form.name.trim() || form.price_tokens_monthly < 1) {
      toast.error('Name and token price required');
      return;
    }
    setSaving(true);
    try {
      // Backend tiers model uses price_monthly (USD) — store price_tokens_monthly as extra
      await axios.post(`${API}/api/subscriptions/tiers`, {
        name: form.name,
        price_monthly: form.price_tokens_monthly / 20,  // maintain USD field = tokens/20
        tokens_included: form.tokens_included,
        free_content_threshold: form.free_content_threshold,
        benefits: form.benefits.split(',').map(s => s.trim()).filter(Boolean),
      }, { withCredentials: true });
      toast.success('Tier created');
      setOpen(false);
      setForm({ name: 'Fan Tier', price_tokens_monthly: 200, tokens_included: 0, free_content_threshold: 0, benefits: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="fan-tiers-tab">
      <div className="flex justify-between items-center">
        <p className="text-slate-400 text-sm">{tiers.length} tier{tiers.length !== 1 && 's'}</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="px-4 py-2 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 flex items-center gap-2"
              data-testid="create-tier-btn">
              <Plus className="w-5 h-5" /> New Tier
            </button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle>Create Fan Tier</DialogTitle>
              <p className="text-xs text-slate-400">Fans pay tokens monthly to unlock your subscriber-only content.</p>
            </DialogHeader>
            <div className="space-y-3 mt-3">
              <div>
                <Label>Tier Name</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="bg-slate-800 border-white/10" data-testid="tier-name" />
              </div>
              <div>
                <Label>Price per month (tokens)</Label>
                <Input type="number" min="1" value={form.price_tokens_monthly}
                  onChange={e => setForm({ ...form, price_tokens_monthly: parseInt(e.target.value) || 0 })}
                  className="bg-slate-800 border-white/10" data-testid="tier-price" />
                <p className="text-xs text-slate-600 mt-1">~${(form.price_tokens_monthly / 20).toFixed(2)} USD equivalent</p>
              </div>
              <div>
                <Label>Benefits (comma-separated)</Label>
                <Input value={form.benefits} onChange={e => setForm({ ...form, benefits: e.target.value })}
                  placeholder="Access to all posts, Priority DM, Weekly live Q&A"
                  className="bg-slate-800 border-white/10" />
              </div>
              <button onClick={create} disabled={saving}
                className="w-full py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 disabled:opacity-50"
                data-testid="save-tier-btn">
                {saving ? 'Saving…' : 'Create Tier'}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {tiers.length === 0 ? (
        <div className="glass-light rounded-2xl p-12 text-center">
          <Star className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500">No fan tiers yet. Create one to start earning recurring token income.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tiers.map(t => (
            <div key={t.id} className="glass-light rounded-2xl p-5" data-testid={`tier-card-${t.id}`}>
              <h4 className="font-heading text-lg font-bold text-white">{t.name}</h4>
              <div className="flex items-baseline gap-1 mt-2">
                <Coins weight="fill" className="w-5 h-5 text-amber-400" />
                <span className="text-2xl font-bold text-white">{Math.round((t.price_monthly || 0) * 20)}</span>
                <span className="text-slate-500 text-sm">tokens/month</span>
              </div>
              {t.benefits?.length > 0 && (
                <ul className="mt-3 space-y-1">
                  {t.benefits.map((b, i) => (
                    <li key={`${t._id || t.id || 'tier'}-${i}-${b}`} className="text-sm text-slate-400">· {b}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FanTiersTab;
